/**
 * Created by vladislav on 9/6/2022
 */

const Microsoft = function (pluginName = "") {
    Platform.call(this, pluginName || connector.MICROSOFT);

    connector.info.os = connector.OS_WINDOWS;
    connector.social.needWindowForLogin = true;
};

Microsoft.prototype = Object.create(Platform.prototype);
Microsoft.prototype.constructor = Microsoft;

Microsoft.prototype._initialize = function (callback) {
    this._subscribeTouchKeyboard();

    this.callNative("app.initialize", (code, response) => {
        if (response && response.deviceId) {
            connector.info.deviceID = response && response.deviceId;
        }

        this.launchUriSupported = Boolean(response && response.launchUri);

        callback();
    });
};

Microsoft.prototype._subscribeTouchKeyboard = function () {
    const isEditable = (el) => {
        if (!el) {
            return false;
        }
        if (el.tagName === "INPUT") {
            const type = (el.type || "text").toLowerCase();
            return !["button", "submit", "reset", "checkbox", "radio", "file", "range", "color", "image", "hidden"].includes(type);
        }
        return el.tagName === "TEXTAREA" || el.isContentEditable === true;
    };

    document.addEventListener("focusin", (e) => {
        if (isEditable(e.target)) {
            this.callNative("keyboard.show");
        }
    }, true);

    document.addEventListener("focusout", (e) => {
        if (isEditable(e.target)) {
            this.callNative("keyboard.hide");
        }
    }, true);
};

Microsoft.prototype.getAccessToken = function () {
    return "microsoft";
};

Microsoft.prototype.canReview = function () {
    return true;
};

Microsoft.prototype._requestReview = function (callback) {
    this.callNative("store.review", undefined, (code, response) => {
        const result = !response || response.error || code !== connector.CODE_SUCCEED ? connector.CODE_FAILED : connector.CODE_SUCCEED;

        callback(result);
    });
};

Microsoft.prototype.isLatestVersionCheckSupported = function () {
    return true;
};

Microsoft.prototype.checkLatestVersion = function (callback) {
    this.callNative("app.checkLatestVersion", undefined, (code, response) => {
        if (code !== connector.CODE_SUCCEED || response && response.error) {
            this.bus.trigger("stream:error", `Microsoft.checkLatestVersion ${JSON.stringify(response && response.error)}`);
            callback(connector.CODE_FAILED);
            return;
        }

        callback(connector.CODE_SUCCEED, response);
    });
};

Microsoft.prototype._parsePayload = function (callback) {
    this.callNative("app.getCampaignId", undefined, (code, response) => {
        let channel = response && response.campaignId || "";

        if (channel) {
            this.bus.trigger("stream:console", "MICROSOFT_CHANNEL", `User ${connector.config.name} ${this.getUserID()} loaded campaignId ${channel}`);
        }

        const params = connector.utils.getRequestParameters();

        if (channel.startsWith(Microsoft.CROSS_ID_CAMPAIGN)) {
            if (this.isNewUser && !params.crossId) {
                params.crossId = channel.slice(Microsoft.CROSS_ID_CAMPAIGN.length);
            }
            channel = "";
        }

        if (channel.startsWith("storeBingAd_")) {
            channel = "bing";
        }

        if (channel) {
            params.channel = channel;
        }
        callback(params);
    });
};

Microsoft.CROSS_ID_CAMPAIGN = "crossid_";

// The payload path on Microsoft Store builds - protocol activation, the Windows counterpart of Instant's switchGameAsync:
//   - a target game owns the URI scheme "cleverapps-<its store product id, lowercase>", declared in its Package.appxmanifest
//     (garden: cleverapps-9n2nxghqn1hg, ladacha: cleverapps-9pb63vl0qkz2); the id is taken from the store link switchGame gets
//   - the source shell launches "cleverapps-<id>://promo?channel=..&crossId=.." through app.launchUri (Launcher.LaunchUriAsync)
//     with the target's store page as the fallback, so a target that is not installed lands on its store page as before
//   - the target shell appends the activation query to the page url, and _parsePayload below reads it next to the campaign id
//   - the shell answers launchUri: true on app.initialize; an older shell never advertises it and this returns false
// switchGame takes the protocol path for a payload with crossId or promo (rewarded promo, game announce); every other switch opens the link as before.
// The release gate is the game config, not this code: REWARDED_PROMO in the availables lists the sources it runs on, and
// "microsoft" goes there only after the target games are in the store with the protocol in their manifests.
//
// How to test (the shell sources are symlinked into every game's Windows project and copied into <game>-ms by the build):
//   1. deploy the source game's web staging - the staging shell loads its JS from the staging url in appsettings.json
//   2. run the microsoftStaging build target of the source game: post-build copies proj.microsoft2.staging into ../<game>-ms
//      and pushes; then run the "Windows UWP Build" workflow by hand (build_staging) and install the MSIX staging artifact
//      (Developer Mode on, install its certificate first); a red workflow is the shell not compiling
//   3. in the staging app's DevTools: connector.platform.canPassPayload() is true;
//      Tool -> rewardedPromo -> startPromotion -> <target>, then gotoGameByLink with the target's store link
//      https://apps.microsoft.com/detail/<id> - with the target installed it opens with the payload, without it the store page opens
//   4. target side alone, on any installed build whose manifest declares the scheme, from PowerShell:
//      Start-Process "cleverapps-<id>://promo?crossId=test1&channel=mergecraft"
//      the game starts, connector.utils.getRequestParameters() shows both values, the shop still loads products
//      (native calls survive a page url with a query); an unknown crossId is dropped by the tracker, which is correct
//   5. full flow: in the target's console cleverapps.rewardedPromoTracker.crossId holds the real id, tasks complete there
//      and become claimable in the source; a target already running comes to the front without the payload (known limit)
Microsoft.prototype.canPassPayload = function () {
    return Boolean(this.launchUriSupported);
};

Microsoft.prototype.switchGame = function (url, payload, callback) {
    callback = callback || function () {};

    const match = typeof url === "string" && url.match(/apps\.microsoft\.com\/detail\/([a-z0-9]+)/i);
    const productId = match && match[1];

    if (productId && payload && payload.crossId && /^https:\/\//i.test(url)) {
        url = connector.utils.addSearchParams(url, { cid: Microsoft.CROSS_ID_CAMPAIGN + payload.crossId });
    }

    if (!this.launchUriSupported || !payload || !productId || !(payload.crossId || payload.promo)) {
        Platform.prototype.switchGame.call(this, url);
        return;
    }

    const query = Object.keys(payload)
        .filter((key) => payload[key] !== undefined)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(payload[key])}`)
        .join("&");

    this.callNative("app.launchUri", { uri: `cleverapps-${productId.toLowerCase()}://promo?${query}`, fallback: url }, (code, response) => {
        if (!response || !response.launched) {
            callback(ERRORS.SWITCH_GAME.UNKNOWN);
        }
    });
};

Microsoft.prototype._callNative = function (method, options) {
    const data = { method, ...options || {} };

    window.chrome.webview.postMessage(JSON.stringify(data));
};

Microsoft.prototype.callCallback = function (method, code, options, callId) {
    try {
        Platform.prototype.callCallback.call(this, method, code, options, callId);
    } catch (e) {
        setTimeout(() => {
            throw e;
        }, 0);
    }
};

Microsoft.prototype.canSwitchGame = function () {
    return true;
};
